/**
 * SIT API client — single axios instance shared by all SIT api_scripts.
 *
 * Token strategy: acquire an Azure access token via MSAL `apiTokenRequest`
 * (scope: `api://{client_id}/access`). The resulting token's `aud` claim is
 * `api://{client_id}`, which matches the SIT API Gateway's JWT authorizer
 * (configured with `azure_jwt_audience = "api://f16befcf-..."` in the
 * Terraform `api` module). ID tokens have `aud = client_id` (bare GUID) and
 * therefore DO NOT validate — must use access tokens here.
 */

import axios from 'axios';
import msalInstance from '../../../shared/config/msalInstance';
import { apiTokenRequest } from '../../../shared/config/authConfig';

const BASE_URL =
  process.env.REACT_APP_SIT_API_BASE ||
  process.env.REACT_APP_SIT_API_URL ||
  '';

async function getBearerToken() {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('Not authenticated — sign in with Azure first.');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account: accounts[0]
    });
    return result.accessToken;
  } catch (silentErr) {
    // Fall back to interactive on InteractionRequiredAuthError.
    const result = await msalInstance.acquireTokenPopup({
      ...apiTokenRequest,
      account: accounts[0]
    });
    return result.accessToken;
  }
}

export const sitApi = axios.create({
  baseURL: BASE_URL,
  // 90s covers vision/Transcribe-bound endpoints (POST /imports, GET
  // /reports). Tighter endpoints can override per-call via { timeout }.
  timeout: 90000,
  headers: { 'content-type': 'application/json' }
});

sitApi.interceptors.request.use(async (config) => {
  if (!BASE_URL) {
    return Promise.reject(
      new Error(
        'REACT_APP_SIT_API_BASE is not set. Configure it in your .env to enable the SIT backend.'
      )
    );
  }
  const token = await getBearerToken();
  config.headers = config.headers ?? {};
  config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

/**
 * Returns true if a SIT backend URL is configured. The UI uses this to gate
 * action buttons that require the backend.
 */
export function isSitBackendConfigured() {
  return Boolean(BASE_URL);
}
